import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  PencilCircle, 
  ArrowsClockwise, 
  Copy,
  Sparkle,
  Lightbulb,
  Hash,
  VideoCamera,
  SquaresFour,
  BookmarkSimple,
  CheckCircle,
  Folder,
  Repeat,
  ArrowRight
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import ReferenceMediaUpload from '../components/ReferenceMediaUpload';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { useAuth } from '../context/AuthContext';
import { ShareButton } from '../components/ShareButton';
import { TextHistoryPanel } from '../components/TextHistoryPanel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { usePersistedInput, usePersistedOutput } from '../hooks/usePersistedState';
import { useNavigate, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Defensively render any LLM output as readable text. The model occasionally
   returns nested objects/arrays (e.g. `body` as an array of `{timestamp, dialogue}`
   segments) which would otherwise crash React with error #31. */
const toText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    return v.map(item => {
      if (item == null) return '';
      if (typeof item === 'string') return item;
      if (typeof item === 'object') {
        // Common LLM shapes: {timestamp, dialogue}, {time, line}, {scene, action}
        const ts = item.timestamp || item.time || item.t || '';
        const line = item.dialogue || item.line || item.text || item.content || item.action || item.description || '';
        if (ts || line) return `${ts ? `[${ts}] ` : ''}${line}`.trim();
        return Object.values(item).filter(x => typeof x === 'string').join(' — ');
      }
      return String(item);
    }).filter(Boolean).join('\n\n');
  }
  if (typeof v === 'object') {
    // Pull obvious "main text" fields, else fall back to JSON
    if (v.dialogue || v.text || v.content || v.line) return toText(v.dialogue || v.text || v.content || v.line);
    try { return JSON.stringify(v, null, 2); } catch { return ''; }
  }
  return '';
};

const platforms = ['Instagram', 'TikTok', 'LinkedIn', 'X (Twitter)', 'Facebook', 'Blog'];
const tones = ['Professional', 'Casual', 'Witty', 'Inspirational', 'Educational', 'Bold', 'Friendly'];

const contentTypes = [
  { id: 'ideas', label: 'Content Ideas', icon: Lightbulb },
  { id: 'caption', label: 'Captions', icon: PencilCircle },
  { id: 'hooks', label: 'Hooks', icon: Sparkle },
  { id: 'hashtags', label: 'Hashtags', icon: Hash },
  { id: 'reel_script', label: 'Reel Scripts', icon: VideoCamera },
  { id: 'carousel', label: 'Carousel', icon: SquaresFour }
];

export const ContentStudioPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const seed = location.state?.seed;
  const [activeTab, setActiveTab] = usePersistedInput('content-studio', 'activeTab', 'ideas');
  const [niche, setNiche] = usePersistedInput('content-studio', 'niche', '');
  const [targetAudience, setTargetAudience] = usePersistedInput('content-studio', 'targetAudience', '');
  const [tone, setTone] = usePersistedInput('content-studio', 'tone', '');
  const [platform, setPlatform] = usePersistedInput('content-studio', 'platform', 'Instagram');
  const [objective, setObjective] = usePersistedInput('content-studio', 'objective', '');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = usePersistedOutput('content-studio', null);
  const [error, setError] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [savedIndex, setSavedIndex] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [library, setLibrary] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [repurposing, setRepurposing] = useState(null);
  const [repurposeResult, setRepurposeResult] = useState(null);
  const [pageTab, setPageTab] = useState('create');

  // Zero Friction: Customise → progressive disclosure
  const [showAdvanced, setShowAdvanced] = useState(false);
  // The single primary user input — "What's it about? (optional)"
  const [topic, setTopic] = useState('');
  // Reference media optional uploads
  const [referenceMedia, setReferenceMedia] = useState([]);
  // Once-per-session guard so we don't auto-generate on every remount
  const [autoWrote, setAutoWrote] = useState(false);

  const handleHistoryReload = (item) => {
    const inputs = item.inputs || {};
    if (inputs.niche) setNiche(inputs.niche);
    if (inputs.platform) setPlatform(inputs.platform);
    if (inputs.content_type) setActiveTab(inputs.content_type);
    if (inputs.target_audience) setTargetAudience(inputs.target_audience);
    if (inputs.tone) setTone(inputs.tone);
    if (inputs.objective) setObjective(inputs.objective);
    const output = item.output || {};
    if (output.content) setResults(output.content);
    setPageTab('create');
  };

  // Brand-profile loaded handler — called by BrandSettingsSummary.
  // When the profile has enough signal, auto-write the first piece of content
  // immediately so the user lands on value, not a blank form.
  const handleBrandProfileLoad = (bp) => {
    if (!bp) return;
    let nextNiche = niche, nextTone = tone, nextAudience = targetAudience;
    if (bp.industry)        { nextNiche = niche || bp.industry; setNiche(prev => prev || bp.industry); }
    if (bp.target_audience) { nextAudience = targetAudience || bp.target_audience; setTargetAudience(prev => prev || bp.target_audience); }
    if (bp.tone_of_voice)   {
      const matched = tones.find(t => t.toLowerCase() === String(bp.tone_of_voice).toLowerCase()) || bp.tone_of_voice;
      nextTone = tone || matched;
      setTone(prev => prev || matched);
    }
    // Auto-write a Caption once per session if we have enough to work with and
    // the user hasn't already produced output / typed a topic.
    if (autoWrote || results || topic || seed) return;
    if (!nextNiche || !nextTone) return;
    try {
      const sessionKey = 'scl_content_studio_autowrote';
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, '1');
    } catch { /* ignore */ }
    setAutoWrote(true);
    // Default to Caption — the highest-value first impression.
    setActiveTab('caption');
    const derivedTopic = `A scroll-stopping caption that introduces ${nextNiche}${nextAudience ? ` to ${nextAudience.split(/[,.]/)[0].trim()}` : ''}`;
    setTimeout(() => {
      // Pass fresh niche/tone/audience AND contentType to bypass the
      // React state-async race (otherwise activeTab='ideas' on first paint).
      generateContent(derivedTopic, {
        contentType: 'caption',
        niche: nextNiche,
        tone: nextTone,
        audience: nextAudience,
      });
    }, 60);
  };

  useEffect(() => {
    // Apply seed from navigation state (idea → creation transfer) once.
    if (seed) {
      if (seed.contentType) setActiveTab(seed.contentType);
      if (seed.topic) { setObjective(seed.topic); setTopic(seed.topic); }
      if (seed.tone) setTone(seed.tone);
      if (seed.platform) setPlatform(seed.platform);
      if (seed.audience) setTargetAudience(seed.audience);
      if (seed.niche) setNiche(seed.niche);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateContent = async (overrideTopic, overrides = {}) => {
    // Topic is optional; we use a brand-derived default when blank.
    const usedTopic = (overrideTopic !== undefined ? overrideTopic : topic).trim();
    // Allow callers (esp. handleBrandProfileLoad) to pass fresh state values
    // so we don't trip the validation guard on stale React state — and so the
    // backend gets the correct content_type even when the tab change hasn't
    // committed yet.
    const usedType     = overrides.contentType ?? activeTab;
    const usedNiche    = overrides.niche    ?? niche;
    const usedTone     = overrides.tone     ?? tone;
    const usedAudience = overrides.audience ?? targetAudience;
    const usedPlatform = overrides.platform ?? platform;
    const usedObjective = usedTopic || objective || `${usedType} for ${usedNiche || 'my brand'}`;

    if (!usedNiche || !usedTone) {
      setShowAdvanced(true);
      setError("Add a niche and tone in Customise to write something specific.");
      return;
    }
    setError('');
    setLoading(true);
    setResults(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/content/generate`, {
        content_type: usedType,
        niche: usedNiche,
        target_audience: usedAudience || 'general audience',
        tone: usedTone,
        platform: usedPlatform,
        objective: usedObjective,
        reference_media: referenceMedia.length ? referenceMedia : undefined,
      });

      setResults(data.content);
    } catch (err) {
      console.error('Failed to generate content:', err);
      setError('Could not write it just now. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const saveToLibrary = async (content, title) => {
    try {
      await axios.post(`${API_URL}/api/library/save`, {
        content_type: activeTab,
        title: title || `${activeTab} - ${new Date().toLocaleDateString()}`,
        content: content,
        tags: [platform, tone, niche].filter(Boolean)
      });
      setSavedIndex(title);
      setTimeout(() => setSavedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to save to library:', err);
    }
  };

  const fetchLibrary = async () => {
    setLoadingLibrary(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/library`);
      setLibrary(data.items || []);
    } catch (err) {
      console.error('Failed to fetch library:', err);
    } finally {
      setLoadingLibrary(false);
    }
  };

  const toggleLibrary = () => {
    if (!showLibrary) {
      fetchLibrary();
    }
    setShowLibrary(!showLibrary);
  };

  const repurposeContent = async (content, contentType) => {
    setRepurposing(contentType);
    setRepurposeResult(null);
    try {
      const text = typeof content === 'string' ? content : JSON.stringify(content);
      const { data } = await axios.post(`${API_URL}/api/content/repurpose`, {
        content: text, content_type: contentType,
        target_formats: ['twitter_thread', 'instagram_caption', 'email_snippet', 'ad_copy', 'linkedin_post']
      });
      setRepurposeResult(data.result);
    } catch (err) { console.error('Repurpose failed:', err); }
    finally { setRepurposing(null); }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const renderResults = () => {
    if (!results) return null;

    switch (activeTab) {
      case 'ideas':
        return (
          <div className="space-y-4">
            {Array.isArray(results) && results.map((item, index) => (
              <div key={index} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 mb-2 inline-block">{item.format}</span>
                    <h3 className="font-medium mb-2">{item.idea}</h3>
                    <p className="text-zinc-400 text-sm">Hook: "{item.hook}"</p>
                    {/* Cross-link to create */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {(item.format || '').toLowerCase().includes('carousel') && (
                        <button onClick={() => navigate('/dashboard/carousel-creator')} className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold" style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.12)' }}>
                          <ArrowRight size={10} /> Create Carousel
                        </button>
                      )}
                      {(item.format || '').toLowerCase().match(/reel|video|clip/) && (
                        <button onClick={() => navigate('/dashboard/ad-creator')} className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold" style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.12)' }}>
                          <ArrowRight size={10} /> Create Video
                        </button>
                      )}
                      <button onClick={() => navigate('/dashboard/template-studio')} className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold" style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.35)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <ArrowRight size={10} /> Create Flyer
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => copyToClipboard(item.idea, index)}
                      className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {copiedIndex === index ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                    </button>
                    <ShareButton title={item.idea} content={`Hook: ${item.hook}\nFormat: ${item.format}`} type="Content Idea" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'caption':
        return (
          <div className="space-y-4">
            {Array.isArray(results) && results.map((item, index) => (
              <div key={index} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-zinc-200 mb-3 whitespace-pre-wrap">{item.caption}</p>
                    <p className="text-zinc-400 text-sm mb-2">CTA: {item.cta}</p>
                    <p className="text-zinc-500 text-xs">{Array.isArray(item.hashtags) ? item.hashtags.join(' ') : item.hashtags}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(`${item.caption}\n\n${item.cta}\n\n${Array.isArray(item.hashtags) ? item.hashtags.join(' ') : item.hashtags}`, index)}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiedIndex === index ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        );

      case 'hooks':
        return (
          <div className="space-y-3">
            {Array.isArray(results) && results.map((item, index) => (
              <div key={index} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 mb-2 inline-block">{item.type}</span>
                  <p className="text-zinc-200 font-medium">"{item.hook}"</p>
                </div>
                <button
                  onClick={() => copyToClipboard(item.hook, index)}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  {copiedIndex === index ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                </button>
              </div>
            ))}
          </div>
        );

      case 'hashtags':
        return (
          <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="font-medium">Generated Hashtags</h3>
              <button
                onClick={() => copyToClipboard(Array.isArray(results) ? results.join(' ') : '', 0)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                {copiedIndex === 0 ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(results) && results.map((tag, index) => (
                <span key={index} className="px-3 py-1.5 rounded-full bg-white/5 text-zinc-300 text-sm">{tag}</span>
              ))}
            </div>
          </div>
        );

      case 'reel_script':
        return (
          <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4 space-y-4 break-words">
            <div className="flex items-start justify-between">
              <h3 className="font-medium">Reel Script</h3>
              <button
                onClick={() => copyToClipboard(`HOOK: ${toText(results.hook)}\n\nBODY: ${toText(results.body)}\n\nCTA: ${toText(results.cta)}\n\nVISUAL CUES: ${toText(results.visual_cues)}`, 0)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                {copiedIndex === 0 ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
              </button>
            </div>
            <div>
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Hook</span>
              <p className="text-zinc-200 mt-1 whitespace-pre-wrap break-words">{toText(results.hook)}</p>
            </div>
            <div>
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Body</span>
              <p className="text-zinc-200 mt-1 whitespace-pre-wrap break-words">{toText(results.body)}</p>
            </div>
            <div>
              <span className="text-xs text-zinc-500 uppercase tracking-wider">CTA</span>
              <p className="text-zinc-200 mt-1 whitespace-pre-wrap break-words">{toText(results.cta)}</p>
            </div>
            <div>
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Visual Cues</span>
              <p className="text-zinc-400 mt-1 text-sm whitespace-pre-wrap break-words">{toText(results.visual_cues)}</p>
            </div>
          </div>
        );

      case 'carousel':
        return (
          <div className="space-y-3">
            {Array.isArray(results) && results.map((slide, index) => (
              <div key={index} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-sm font-medium">
                    {slide.slide_number}
                  </div>
                  <div className="flex-1">
                    <p className="text-zinc-200 mb-2">{slide.content}</p>
                    <p className="text-sm">Visual: {slide.visual_suggestion}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(slide.content, index)}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiedIndex === index ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return <pre className="text-zinc-400 text-sm overflow-auto">{JSON.stringify(results, null, 2)}</pre>;
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="content-studio-page" className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <PencilCircle size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Content Studio</h1>
              <p className="text-zinc-400 text-sm">Captions, scripts, and hooks — in your voice</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setPageTab(t)} data-testid={`page-tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: pageTab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${pageTab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: pageTab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'History'}
              </button>
            ))}
          </div>
        </motion.div>

        {pageTab === 'create' && (
        <>
        {/* Content Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-1 flex flex-wrap gap-1 overflow-x-auto">
            {contentTypes.map((type) => {
              const Icon = type.icon;
              return (
                <TabsTrigger
                  key={type.id}
                  value={type.id}
                  data-testid={`tab-${type.id}`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400 whitespace-nowrap"
                >
                  <Icon size={14} />
                  <span>{type.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Input Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-1"
            >
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5 sticky top-24">
                <BrandSettingsSummary
                  fields={['brand', 'tone', 'audience']}
                  onLoad={handleBrandProfileLoad}
                  compact
                />

                {seed && (seed.topic || seed.contentType) && (
                  <div className="rounded-xl p-3" data-testid="seed-banner"
                    style={{ background: 'rgba(200,240,96,0.05)', border: '1px solid rgba(200,240,96,0.15)' }}>
                    <p className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: '#c8f060' }}>Pre-filled from idea</p>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.7)' }}>
                      {seed.topic || (seed.contentType && `Writing ${seed.contentType}`)}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-2" style={{ color: 'rgba(245,240,232,0.4)', fontFamily: 'ui-monospace, monospace' }}>What's it about? (optional)</label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    data-testid="content-topic-input"
                    placeholder="Leave blank — we'll pick the best topic for your audience"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                  />
                </div>

                {error && (
                  <div data-testid="content-error" className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2">{error}</div>
                )}

                <button
                  onClick={() => generateContent()}
                  disabled={loading}
                  data-testid="generate-content-btn"
                  className="w-full rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  style={{ background: '#c8f060', color: '#080808' }}
                >
                  {loading ? (
                    <>
                      <ArrowsClockwise size={18} className="animate-spin" />
                      Writing your {activeTab}…
                    </>
                  ) : (
                    <>
                      <Sparkle size={18} weight="fill" />
                      Write it
                    </>
                  )}
                </button>

                <ReferenceMediaUpload
                  label="Add reference media (optional)"
                  note="Upload a brand reference, mood image, or doc to shape the writing."
                  acceptedTypes={['image', 'document']}
                  maxFiles={3}
                  maxSizeMB={25}
                  onChange={setReferenceMedia}
                />

                <button onClick={() => setShowAdvanced(v => !v)} data-testid="content-customise-toggle"
                  className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {showAdvanced ? 'Hide customise' : 'Customise →'}
                  </span>
                  <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
                </button>

                {showAdvanced && (
                  <div className="space-y-4 pt-1">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Niche / Business</label>
                      <input
                        type="text"
                        value={niche}
                        onChange={(e) => setNiche(e.target.value)}
                        data-testid="niche-input"
                        placeholder="e.g., Sustainable fashion brand"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Target Audience</label>
                      <input
                        type="text"
                        value={targetAudience}
                        onChange={(e) => setTargetAudience(e.target.value)}
                        data-testid="audience-input"
                        placeholder="e.g., Women 25-40, eco-conscious"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Tone of Voice</label>
                      <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger data-testid="tone-select" className="w-full bg-zinc-900 border-white/10 text-white">
                          <SelectValue placeholder="Select tone" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/10">
                          {tones.map((t) => (
                            <SelectItem key={t} value={t} className="text-white hover:bg-white/10">{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Platform</label>
                      <Select value={platform} onValueChange={setPlatform}>
                        <SelectTrigger data-testid="platform-select" className="w-full bg-zinc-900 border-white/10 text-white">
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/10">
                          {platforms.map((p) => (
                            <SelectItem key={p} value={p} className="text-white hover:bg-white/10">{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Objective override</label>
                      <input
                        type="text"
                        value={objective}
                        onChange={(e) => setObjective(e.target.value)}
                        placeholder="e.g., Drive traffic to new collection"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-medium font-['Instrument_Serif',serif]">Your Content</h2>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={toggleLibrary}
                      data-testid="library-btn"
                      className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors px-3 py-1.5 rounded-lg hover:bg-white/5"
                    >
                      <Folder size={18} />
                      Library
                    </button>
                    {results && (
                      <button 
                        onClick={() => saveToLibrary(results, `${contentTypes.find(t => t.id === activeTab)?.label} - ${platform}`)}
                        data-testid="save-content-btn"
                        className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors px-3 py-1.5 rounded-lg hover:bg-white/5"
                      >
                        {savedIndex ? <CheckCircle size={18} className="text-green-400" /> : <BookmarkSimple size={18} />}
                        {savedIndex ? 'Saved!' : 'Save'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Library Panel */}
                {showLibrary && (
                  <div className="mb-6 bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-sm">Saved Content</h3>
                      <button onClick={() => setShowLibrary(false)} className="text-zinc-400 hover:text-white text-xs">Close</button>
                    </div>
                    {loadingLibrary ? (
                      <div className="flex items-center justify-center py-8">
                        <ArrowsClockwise size={20} className="animate-spin text-zinc-400" />
                      </div>
                    ) : library.length === 0 ? (
                      <p className="text-sm text-center py-4">No saved content yet</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {library.map((item, i) => (
                          <div key={item.id || i} className="p-3 bg-zinc-900/50 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-zinc-500">{item.content_type}</span>
                              <div className="flex items-center gap-2">
                                <button onClick={() => repurposeContent(item.content, item.content_type)} data-testid={`repurpose-lib-${i}`}
                                  className="text-xs text-zinc-500 hover:text-white transition-colors flex items-center gap-1">
                                  <Repeat size={12} /> Repurpose
                                </button>
                                <span className="text-xs text-zinc-600">{new Date(item.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <p className="text-sm text-zinc-300 truncate">{item.title}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!results ? (
                  <div className="text-center py-16">
                    <PencilCircle size={48} className="mx-auto mb-4 text-zinc-700" />
                    <h3 className="text-lg font-medium mb-2">No content generated yet</h3>
                    <p className="text-sm">Fill in your details and click generate</p>
                  </div>
                ) : (
                  renderResults()
                )}

                {results && (
                  <div className="mt-6 space-y-3">
                    <UpgradePrompt type="post-generation" tier={user?.subscription?.tier || 'starter'} />
                    <div className="flex gap-3">
                    <button onClick={generateContent} disabled={loading}
                      className="flex-1 py-2.5 text-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white text-sm transition-colors flex items-center justify-center gap-2">
                      <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} /> Regenerate
                    </button>
                    <button onClick={() => repurposeContent(JSON.stringify(results), activeTab)} disabled={!!repurposing} data-testid="repurpose-results-btn"
                      className="flex-1 py-2.5 text-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white text-sm transition-colors flex items-center justify-center gap-2">
                      {repurposing ? <><ArrowsClockwise size={16} className="animate-spin" /> Repurposing...</> : <><Repeat size={16} /> Repurpose</>}
                    </button>
                  </div>
                  </div>
                )}

                {repurposeResult && (
                  <div className="mt-4 bg-zinc-800/30 border border-white/5 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium flex items-center gap-2"><Repeat size={16} /> Repurposed Content</h3>
                      <button onClick={() => setRepurposeResult(null)} className="text-xs text-zinc-500 hover:text-white">Close</button>
                    </div>
                    {repurposeResult.repurposed?.map((item, i) => (
                      <div key={i} className="p-3 bg-zinc-900/50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-zinc-400">{item.format}</span>
                          <span className="text-xs text-zinc-500">{item.platform}</span>
                        </div>
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap">{item.content}</p>
                        {item.tips && <p className="text-xs text-zinc-500 mt-1">{item.tips}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </Tabs>
        </>
        )}

        {/* History Tab */}
        {pageTab === 'history' && (
          <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Content History</h2>
            <TextHistoryPanel toolType="content_studio" onReload={handleHistoryReload} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ContentStudioPage;
